import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FiPlus, FiSearch, FiEdit2, FiCopy } from 'react-icons/fi';
import POSButton from '@/components/POS/Common/POSButton';
import POSInput from '@/components/POS/Common/POSInput';
import POSSwitch from '@/components/POS/Common/POSSwitch';
import POSTable, { ColumnType, RowType } from '@/components/POS/Common/POSTable';
import POSDeleteModal from '@/components/POS/Common/POSDeleteModal';
import { GetApiListBookingDiscountsSortBy } from '@/shared/api/models/getApiListBookingDiscountsSortBy';
import { GetApiListBookingDiscountsSortOrder } from '@/shared/api/models/getApiListBookingDiscountsSortOrder';
import { BookingDiscount, DiscountChannel } from '../Types/booking-discounts.types';
import { bookingDiscountsApi } from '../Core/booking-discounts.api';
import DeleteIcon from '../../../assets/Delete.svg';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

export default function BookingDiscountsList() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [discountsList, setDiscountsList] = useState<BookingDiscount[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedKeyword, setDebouncedKeyword] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [sortBy, setSortBy] = useState<GetApiListBookingDiscountsSortBy>(
        GetApiListBookingDiscountsSortBy.createdAt,
    );
    const [sortOrder, setSortOrder] = useState<GetApiListBookingDiscountsSortOrder>(
        GetApiListBookingDiscountsSortOrder.desc,
    );
    const [selectedForDelete, setSelectedForDelete] = useState<BookingDiscount | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setDebouncedKeyword(searchQuery.trim());
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(debounceTimer);
    }, [searchQuery]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedKeyword]);

    const loadList = useCallback(async () => {
        setIsLoading(true);
        try {
            const listData = await bookingDiscountsApi.listBookingDiscounts({
                page: currentPage,
                limit: PAGE_SIZE,
                sortBy,
                sortOrder,
                ...(debouncedKeyword ? { keyword: debouncedKeyword } : {}),
            });
            setDiscountsList(listData.discounts as BookingDiscount[]);
            setTotalPages(listData.totalPages);
            setTotalItems(listData.total);

            if (listData.totalPages > 0 && currentPage > listData.totalPages) {
                setCurrentPage(listData.totalPages);
            }
        } catch (error) {
            console.error('Error fetching booking discounts list:', error);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, sortBy, sortOrder, debouncedKeyword]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    const handleSortChange = (columnId: string) => {
        const isValidSortField = Object.values(GetApiListBookingDiscountsSortBy).includes(
            columnId as GetApiListBookingDiscountsSortBy,
        );
        if (!isValidSortField) {
            return;
        }

        const nextSortBy = columnId as GetApiListBookingDiscountsSortBy;
        const nextSortOrder =
            sortBy === nextSortBy && sortOrder === GetApiListBookingDiscountsSortOrder.desc
                ? GetApiListBookingDiscountsSortOrder.asc
                : GetApiListBookingDiscountsSortOrder.desc;

        setSortBy(nextSortBy);
        setSortOrder(nextSortOrder);
        setCurrentPage(1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((previousPage) => previousPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((previousPage) => previousPage - 1);
        }
    };

    const handleToggleActive = async (discountItem: BookingDiscount, nextActiveState: boolean) => {
        try {
            await bookingDiscountsApi.updateBookingDiscount(discountItem.id, {
                isActive: nextActiveState,
            });
            toast.success(t('BookingDiscounts.ToastUpdatedSuccess'));
            loadList();
        } catch (error) {
            console.error('Error toggling booking discount status:', error);
            toast.error(t('BookingDiscounts.ToastErrorSave'));
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedForDelete) {
            return;
        }
        setIsDeleting(true);
        try {
            await bookingDiscountsApi.deleteBookingDiscount(selectedForDelete.id);
            toast.success(t('BookingDiscounts.ToastDeletedSuccess'));
            setSelectedForDelete(null);
            loadList();
        } catch (error) {
            console.error('Error deleting booking discount:', error);
            toast.error(t('BookingDiscounts.ToastErrorSave'));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCopyCode = (codeString: string) => {
        navigator.clipboard.writeText(codeString);
        toast.info(`${t('BookingDiscounts.CodeHeader')}: ${codeString}`);
    };

    const formatDiscountValue = (discountItem: BookingDiscount): string => {
        const numericValue = Number(discountItem.discountValue) || 0;
        if (discountItem.discountType === 'PERCENTAGE') {
            return `${numericValue}%`;
        }
        return `${numericValue} DKK`;
    };

    const formatChannelLabel = (channelItem: DiscountChannel): string => {
        switch (channelItem) {
            case 'ONLINE_BOOKING':
                return t('BookingDiscounts.ChannelOnlineBooking');
            case 'GIFT_CARD':
                return t('BookingDiscounts.ChannelGiftCard');
            case 'PUNCH_CARD':
                return t('BookingDiscounts.ChannelPunchCard');
            default:
                return channelItem;
        }
    };

    const formatCustomerLimit = (discountItem: BookingDiscount): string => {
        const customerType = discountItem.customerType || 'ALL';
        const maxUses = discountItem.maxUsesPerCustomer;
        const detailParts: string[] = [];

        if (customerType === 'NEW') {
            detailParts.push(t('BookingDiscounts.LimitNew'));
        } else if (customerType === 'EXISTING') {
            detailParts.push(t('BookingDiscounts.LimitExisting'));
        } else {
            detailParts.push(t('BookingDiscounts.LimitAll'));
        }

        if (maxUses && maxUses > 0) {
            detailParts.push(`${maxUses}x`);
        } else {
            detailParts.push(t('BookingDiscounts.LimitNo'));
        }

        return detailParts.join(' • ');
    };

    const columns: ColumnType[] = [
        {
            id: 'code',
            name: t('BookingDiscounts.CodeHeader'),
            sortable: true,
            selector: (row: RowType): React.ReactNode => {
                const discountItem = row as unknown as BookingDiscount;
                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            handleCopyCode(discountItem.code);
                        }}
                        sx={{
                            display: 'inline-flex',
                            width: 'fit-content',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.selected' },
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.9rem', letterSpacing: 0.3 }}
                        >
                            {discountItem.code}
                        </Typography>
                        <FiCopy size={13} />
                    </Stack>
                );
            },
        },
        {
            id: 'discountValue',
            name: t('BookingDiscounts.DiscountHeader'),
            sortable: true,
            selector: (row: RowType): React.ReactNode => {
                const discountItem = row as unknown as BookingDiscount;
                return <Typography variant="body2">{formatDiscountValue(discountItem)}</Typography>;
            },
        },
        {
            id: 'channels',
            name: t('BookingDiscounts.ChannelsHeader'),
            selector: (row: RowType): React.ReactNode => {
                const discountItem = row as unknown as BookingDiscount;
                const channelsText = (discountItem.channels || [])
                    .map((channelItem: DiscountChannel) => formatChannelLabel(channelItem))
                    .join(', ');
                return (
                    <Typography variant="body2" color="text.secondary">
                        {channelsText || '—'}
                    </Typography>
                );
            },
        },
        {
            id: 'customerType',
            name: t('BookingDiscounts.CustomerLimitHeader'),
            sortable: true,
            selector: (row: RowType): React.ReactNode => {
                const discountItem = row as unknown as BookingDiscount;
                return (
                    <Typography variant="body2" color="text.secondary">
                        {formatCustomerLimit(discountItem)}
                    </Typography>
                );
            },
        },
        {
            id: 'expiresAt',
            name: t('BookingDiscounts.ExpiresHeader'),
            sortable: true,
            selector: (row: RowType): React.ReactNode => {
                const discountItem = row as unknown as BookingDiscount;
                if (!discountItem.expiresAt) {
                    return (
                        <Typography variant="body2" color="text.secondary">
                            {t('BookingDiscounts.NoExpiry')}
                        </Typography>
                    );
                }
                return (
                    <Typography variant="body2" color="text.secondary">
                        {moment(discountItem.expiresAt).format('DD/MM/YYYY')}
                    </Typography>
                );
            },
        },
        {
            id: 'isActive',
            name: t('BookingDiscounts.StatusHeader'),
            sortable: true,
            selector: (row: RowType): React.ReactNode => {
                const discountItem = row as unknown as BookingDiscount;
                return (
                    <Box
                        onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                        }}
                        onMouseDown={(event: React.MouseEvent) => {
                            event.stopPropagation();
                        }}
                        sx={{ display: 'inline-flex', width: 'fit-content' }}
                    >
                        <POSSwitch
                            checked={discountItem.isActive}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                event.stopPropagation();
                                handleToggleActive(discountItem, event.target.checked);
                            }}
                        />
                    </Box>
                );
            },
        },
        {
            id: 'actions',
            name: t('BookingDiscounts.ActionsHeader'),
            selector: (row: RowType): React.ReactNode => {
                const discountItem = row as unknown as BookingDiscount;
                return (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <IconButton
                            disableFocusRipple
                            disableTouchRipple
                            disableRipple
                            size="small"
                            onClick={(event: React.MouseEvent) => {
                                event.stopPropagation();
                                navigate(`/booking-discounts/${discountItem.id}`);
                            }}
                        >
                            <FiEdit2 size={16} />
                        </IconButton>
                        <IconButton
                            disableFocusRipple
                            disableTouchRipple
                            disableRipple
                            size="small"
                            onClick={(event: React.MouseEvent) => {
                                event.stopPropagation();
                                setSelectedForDelete(discountItem);
                            }}
                        >
                            <img src={DeleteIcon} alt="Delete" />
                        </IconButton>
                    </Stack>
                );
            },
        },
    ];

    return (
        <Stack spacing={3} sx={{ width: '100%', py: 2 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                spacing={2}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {t('BookingDiscounts.Title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t('BookingDiscounts.Subtitle')}
                    </Typography>
                </Box>
                <POSButton
                    variant="save"
                    title={
                        <Stack direction="row" spacing={2} alignItems="center">
                            <FiPlus size={18} />
                            {t('BookingDiscounts.NewDiscountCode')}
                        </Stack>
                    }
                    onClick={() => navigate('/booking-discounts/new')}
                />
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, maxWidth: 400 }}>
                    <POSInput
                        placeholder={t('BookingDiscounts.SearchPlaceholder')}
                        value={searchQuery}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value)}
                        startIcon={<FiSearch size={16} />}
                    />
                </Box>
            </Stack>

            <POSTable
                columns={columns}
                data={discountsList}
                loading={isLoading}
                emptyText={t('BookingDiscounts.NoDiscountCodesFound')}
                onRowClick={(row: RowType) => navigate(`/booking-discounts/${row.id}`)}
                isServerSorting
                onSort={handleSortChange}
                defaultOrder={sortBy}
                serverSortOrder={sortOrder}
            />

            {totalPages > 0 && (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ sm: 'center' }}
                    spacing={1}
                    sx={{
                        p: 2,
                        border: '1px solid #D9D9D9',
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t('Common.Showing')} {discountsList.length} {t('Common.of')} {totalItems} {t('Common.items')}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                            {t('Common.Page')} {currentPage} {t('Common.of')} {Math.max(totalPages, 1)}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handlePreviousPage}
                            disabled={currentPage <= 1 || isLoading}
                            aria-label={t('Common.Page')}
                        >
                            <ChevronLeft />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleNextPage}
                            disabled={currentPage >= totalPages || isLoading}
                            aria-label={t('Common.Page')}
                        >
                            <ChevronRight />
                        </IconButton>
                    </Stack>
                </Stack>
            )}

            {selectedForDelete && (
                <POSDeleteModal
                    open={!!selectedForDelete}
                    handleClose={() => setSelectedForDelete(null)}
                    onClickDismiss={() => setSelectedForDelete(null)}
                    onClickConfirm={handleDeleteConfirm}
                    title={t('BookingDiscounts.DeleteTitle')}
                    description={`${t('BookingDiscounts.DeleteConfirmText')} "${selectedForDelete.code}"?`}
                    disabled={isDeleting}
                />
            )}
        </Stack>
    );
}
